import { useEffect, useState } from "react";
import {
  AspectRatio,
  Button,
  Modal,
  Select,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconArrowLeft } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import type { FileDetails } from "../../types";
import "@mantine/dropzone/styles.css";
import "./UploadDocModal.scss";
import FileDropzone from "./FileDropzone/FileDropzone";
import { forms } from "../../constants";
import ConfirmCloseModal from "../ConfirmCloseModal/ConfirmCloseModal";
import useSelectedYearStore from "../../utils/stores/selectedYear";
import { generateTaxYear } from "../../utilities";
import PDFViewer from "../PdfViewer/PDFViewer";
import ImageViewer from "../ImageViewer/ImageViewer";
import SpreadsheetViewer from "../SpreadsheetViewer/SpreadsheetViewer";
import { useMediaQuery } from "@mantine/hooks";

export enum UploadButtonVariant {
  PRIMARY = "primary",
  SECONDARY = "secondary",
}

interface UploadDocModalProps {
  modalOpened: boolean;
  setModalOpened: (modalOpened: boolean) => void;
  onDocumentUploaded: (file: File, formType: string, isSpouse: boolean) => void;
  onUploadClick?: () => void;
  uploadButtonVariant: UploadButtonVariant;
  previewFile?: File | null;
  // For viewing already-uploaded documents
  presignedUrl?: string | null;
  documentName?: string | null;
  viewOnly?: boolean; // Hide upload controls when true
}

const UploadDocModal = ({
  modalOpened,
  setModalOpened,
  onDocumentUploaded,
  onUploadClick,
  uploadButtonVariant,
  previewFile,
  presignedUrl,
  documentName,
  viewOnly = false,
}: UploadDocModalProps) => {
  const { selectedYear, setSelectedYear } = useSelectedYearStore();

  const [formData, setFormData] = useState({
    selectedFormType: null as string | null,
  });

  const [fileData, setFileData] = useState({
    fileDetails: [] as FileDetails[],
    file: null as File | null,
  });

  const [preview, setPreview] = useState({
    file: null as File | null,
    previewUrl: null as string | null,
  });

  const [confirmOpened, setConfirmOpened] = useState(false);

  const generateTaxYears = () => {
    const currentYear = generateTaxYear();
    const taxYears = [];
    const backYears = 8;
    for (let i = 0; i < backYears; i++) {
      taxYears.push(currentYear - i);
    }
    return taxYears;
  };

  const clearModal = () => {
    setFormData({ selectedFormType: null });
    setFileData({ fileDetails: [], file: null });
    setPreview({ file: null, previewUrl: null });
    setModalOpened(false);
  };

  const handleCloseModal = () => {
    setConfirmOpened(false);
    setModalOpened(false);
  };

  const handleDocModalClose = () => {
    // In view-only mode, just close without confirmation
    if (viewOnly) {
      clearModal();
      return;
    }
    setConfirmOpened(true);
  };

  // Clear modal internal state when modal closes
  useEffect(() => {
    if (!modalOpened) {
      // Reset all internal state when modal closes
      setFormData({ selectedFormType: null });
      setFileData({ fileDetails: [], file: null });
      setPreview({ file: null, previewUrl: null });
    }
  }, [modalOpened]);

  useEffect(() => {
    // If we have a presigned URL (viewing uploaded document), use that
    if (presignedUrl) {
      setPreview({
        file: null,
        previewUrl: presignedUrl,
      });
      return;
    }

    // Otherwise create blob URL from file (uploading new document)
    if (previewFile) {
      const url = URL.createObjectURL(previewFile);
      setPreview((prevPreview) => ({
        ...prevPreview,
        file: previewFile,
        previewUrl: url,
      }));

      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setPreview((prevPreview) => ({
        ...prevPreview,
        previewUrl: null,
      }));
    }
  }, [previewFile, presignedUrl]);

  /**
   * @deprecated This modal should not handle uploads directly.
   * Parent components should provide onUploadClick prop to handle uploads.
   * This ensures consistent upload handling via useDocumentUpload hook.
   */
  const handleFileUpload = async () => {
    if (!fileData.file) {
      console.error("Please select a file");
      return;
    }

    console.warn(
      "[UploadDocModal] Direct upload is deprecated. Parent should handle uploads via onUploadClick prop."
    );

    // Just call the callback - parent component handles actual upload
    if (fileData.file) {
      onDocumentUploaded(
        fileData.file,
        formData.selectedFormType as string,
        false
      );
      clearModal();
    } else {
      console.error("File is null after upload");
    }
  };

  const setFile = (files: File[]) => {
    try {
      const newFileDetails = files.map((file: File) => ({
        name: file.name,
        size: file.size,
        type: file.type,
        extension: file.name.split(".").pop(),
        file: file,
      }));

      setFileData((prevFileData) => ({
        ...prevFileData,
        fileDetails: [...prevFileData.fileDetails, ...newFileDetails],
        file: files[0],
      }));
    } catch (error) {
      console.error(error);
    }
  };

  const handlePdfError = () => {
    notifications.show({
      title: "Invalid PDF",
      message:
        "The PDF file could not be loaded. It may be corrupted or have an invalid structure.",
      color: "red",
    });
    clearModal();
  };

  const renderPreview = (isMobile = false) => {
    if (!preview.previewUrl) return null;

    // Determine file type from File object or URL extension
    let fileType: string;
    if (preview.file) {
      fileType = preview.file.type;
    } else if (presignedUrl) {
      // Extract extension from URL or documentName
      const fileName = documentName || presignedUrl;
      const extension = fileName.split(".").pop()?.toLowerCase() || "";

      // Map extension to MIME type
      if (["jpg", "jpeg", "png", "gif", "bmp", "webp"].includes(extension)) {
        fileType = "image/" + extension;
      } else if (extension === "pdf") {
        fileType = "application/pdf";
      } else if (["xlsx", "xls"].includes(extension)) {
        fileType =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      } else if (extension === "csv") {
        fileType = "text/csv";
      } else {
        fileType = "";
      }
    } else {
      return <Text>Preview not available.</Text>;
    }

    if (fileType.startsWith("image/")) {
      // On mobile, wrap in container and simplify controls
      if (isMobile) {
        return (
          <div className="mobile-image-viewer-container">
            <ImageViewer
              src={preview.previewUrl!}
              simplifiedControls={true}
              disableDrag={true}
            />
          </div>
        );
      }

      // Desktop: regular image viewer
      return <ImageViewer src={preview.previewUrl!} />;
    } else if (fileType === "application/pdf") {
      // On mobile, no AspectRatio wrapper and simplified controls
      if (isMobile) {
        return (
          <div className="mobile-pdf-viewer-container">
            <PDFViewer
              file={preview.previewUrl!}
              onError={handlePdfError}
              simplifiedControls={true}
              disableDrag={true}
            />
          </div>
        );
      }

      // Desktop: keep AspectRatio
      return (
        <AspectRatio ratio={16 / 9} className="preview-pdf-container">
          <PDFViewer file={preview.previewUrl!} onError={handlePdfError} />
        </AspectRatio>
      );
    } else if (
      fileType ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      fileType === "application/vnd.ms-excel" ||
      fileType === "text/csv"
    ) {
      return (
        <div className="preview-spreadsheet-container">
          <SpreadsheetViewer fileUrl={preview.previewUrl!} />
        </div>
      );
    } else {
      return <Text>Preview not available for this file type.</Text>;
    }
  };

  const variantClassName =
    uploadButtonVariant === UploadButtonVariant.SECONDARY
      ? "upload-variant-secondary"
      : "upload-variant-primary";

  // Use both useMediaQuery and a fallback check
  const mediaQueryMobile = useMediaQuery("(max-width: 768px)", undefined, {
    getInitialValueInEffect: false,
  });
  const windowMobile =
    typeof window !== "undefined" && window.innerWidth <= 768;
  const isMobile = mediaQueryMobile || windowMobile;

  const modalTitle = viewOnly
    ? documentName || "Document Viewer"
    : uploadButtonVariant === UploadButtonVariant.PRIMARY
      ? "Upload Documents"
      : "Document Preview";

  // Mobile full-screen view
  if (isMobile && modalOpened) {
    const handleMobileBack = () => {
      // On mobile, close directly without confirmation for better UX
      clearModal();
    };

    return (
      <>
        <div className="upload-doc-mobile-fullscreen">
          {/* Fixed Header */}
          <header className="mobile-header">
            <button
              className="back-button"
              onClick={handleMobileBack}
              aria-label="Go back"
            >
              <IconArrowLeft size={24} />
            </button>
            <h2 className="mobile-title">{modalTitle}</h2>
          </header>

          {/* Scrollable Content */}
          <main className="mobile-content">
            {renderPreview(true)}

            {!viewOnly &&
              uploadButtonVariant === UploadButtonVariant.PRIMARY && (
                <>
                  <div className="mobile-form-section">
                    <Select
                      label="Select Tax Year"
                      placeholder="Pick one"
                      data={generateTaxYears().map((year) => ({
                        value: year.toString(),
                        label: year.toString(),
                      }))}
                      value={selectedYear?.toString() ?? ""}
                      onChange={(value) =>
                        setSelectedYear(
                          value ? parseInt(value, 10) : generateTaxYear()
                        )
                      }
                      size="md"
                      required
                    />
                    <Select
                      label="Select Form Type"
                      placeholder="Pick one"
                      data={forms.map((form) => ({
                        value: form,
                        label: form,
                      }))}
                      value={formData.selectedFormType}
                      onChange={(value) =>
                        setFormData((prev) => ({
                          ...prev,
                          selectedFormType: value,
                        }))
                      }
                      size="md"
                      required
                    />
                  </div>
                  <div className="mobile-dropzone">
                    <FileDropzone
                      setFile={setFile}
                      fileDetails={fileData.fileDetails}
                    />
                  </div>
                </>
              )}
          </main>

          {/* Fixed Footer */}
          <footer className="mobile-footer">
            {viewOnly ? (
              <Button
                fullWidth
                size="lg"
                className="close-button"
                onClick={handleMobileBack}
              >
                Close
              </Button>
            ) : uploadButtonVariant === UploadButtonVariant.PRIMARY ? (
              <>
                <Button
                  fullWidth
                  size="lg"
                  variant="outline"
                  className="cancel-button"
                  onClick={handleMobileBack}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  size="lg"
                  disabled={!selectedYear || !formData.selectedFormType}
                  className="upload-button"
                  onClick={onUploadClick ?? handleFileUpload}
                >
                  Upload
                </Button>
              </>
            ) : (
              <>
                <Button
                  fullWidth
                  size="lg"
                  variant="outline"
                  className="cancel-button"
                  onClick={handleMobileBack}
                >
                  Cancel
                </Button>
                <Button
                  fullWidth
                  size="lg"
                  disabled={!previewFile}
                  className="upload-button"
                  onClick={onUploadClick ?? handleFileUpload}
                >
                  Upload
                </Button>
              </>
            )}
          </footer>
        </div>
      </>
    );
  }

  // Desktop modal view
  return (
    <>
      <Modal
        centered
        size="50%"
        opened={modalOpened}
        onClose={handleDocModalClose}
        overlayProps={{
          backgroundOpacity: 0.55,
          blur: 3,
        }}
        withCloseButton={false}
        className={`upload-doc-modal ${variantClassName}`}
        title={
          <div>
            <Text size="xl" fw={700} mb={"0.5rem"}>
              {modalTitle}
            </Text>
          </div>
        }
        styles={{
          inner: {
            maxWidth: "90vw",
          },
          body: {
            maxHeight: "90vh",
          },
        }}
      >
        <div className="doc-modal">
          {renderPreview(false)}
          {!viewOnly && uploadButtonVariant === UploadButtonVariant.PRIMARY && (
            <div className="doc-modal-form">
              <Select
                label="Select Tax Year"
                placeholder="Pick one"
                data={generateTaxYears().map((year) => ({
                  value: year.toString(),
                  label: year.toString(),
                }))}
                value={selectedYear?.toString() ?? ""}
                onChange={(value) =>
                  setSelectedYear(
                    value ? parseInt(value, 10) : generateTaxYear()
                  )
                }
                style={{ marginBottom: "1rem" }}
                required
              />
              <Select
                label="Select Form Type"
                placeholder="Pick one"
                data={forms.map((form) => ({
                  value: form,
                  label: form,
                }))}
                value={formData.selectedFormType}
                onChange={(value) =>
                  setFormData((prev) => ({
                    ...prev,
                    selectedFormType: value,
                  }))
                }
                style={{ marginBottom: "1rem" }}
                required
              />
            </div>
          )}
          {!viewOnly && uploadButtonVariant === UploadButtonVariant.PRIMARY && (
            <FileDropzone
              setFile={setFile}
              fileDetails={fileData.fileDetails}
            />
          )}
        </div>
        <div className="modal-buttons">
          <Button
            size="md"
            className="cancel-btn"
            onClick={handleDocModalClose}
          >
            {viewOnly ? "Close" : "Cancel"}
          </Button>
          {!viewOnly &&
            (uploadButtonVariant === UploadButtonVariant.PRIMARY ? (
              <Tooltip
                label={
                  !selectedYear || !formData.selectedFormType
                    ? "Please select tax year and form type"
                    : "Upload your tax document"
                }
              >
                <Button
                  size="md"
                  disabled={!selectedYear || !formData.selectedFormType}
                  className="uploadButton"
                  onClick={onUploadClick ?? handleFileUpload}
                >
                  Upload
                </Button>
              </Tooltip>
            ) : (
              <Button
                size="md"
                disabled={!previewFile}
                className="uploadButton"
                onClick={onUploadClick ?? handleFileUpload}
              >
                Upload
              </Button>
            ))}
        </div>
      </Modal>
      <ConfirmCloseModal
        modalOpened={confirmOpened}
        closeModal={handleCloseModal}
        handleSelectNo={() => setConfirmOpened(false)}
      />
    </>
  );
};

export default UploadDocModal;
